import { RealityCheckOptions, UrlParams } from "@gluckgames/commonui";
import { Contract } from "appworks/utils/contracts/contract";
import { logger } from "appworks/utils/logger";
import { RaceHandler } from "appworks/utils/race-handler";
import { Timer } from "appworks/utils/timer";
import axios from "axios";
import Logger = require("js-logger");
import { isArray } from "lodash";
import * as queryString from "query-string";
import { slotModel } from "slotworks/model/slot-model";
import { Jurisdiction } from "slotworks/services/jurisdiction/jurisdiction-service";
import { GamevyFeatureFlagsSpinDelays } from "./integration/schema/gamevy-config-response-schema";

declare const __DEBUG__: boolean;
declare const __ENVIRONMENT__: string;

export enum Modes {
    FUN = "fun",
    REAL = "real"
}

export enum FeatureFlag {
    AUTO_SPIN_LIMIT = "autoSpinLimit",
    SPIN_DELAYS = "spinDelays",
    AUTO_SPIN_ENABLED = "autoSpinEnabled",
    TURBO_AVAILABLE = "turboAvailable",
    AUTO_SPIN_LIMIT_OPTIONS = "autoSpinLimitOptions",
    SPEED_LIMITED = "speedLimited",
    SESSION_TIMER_ENABLED = "sessionTimerEnabled"
}

export type FeatureFlagType = boolean | GamevyFeatureFlagsSpinDelays[];

export const gamevyJurisdiction: Map<string, Jurisdiction> = new Map([
    ["uk", Jurisdiction.UK],
    ["mt", Jurisdiction.Malta],
    ["se", Jurisdiction.Sweden],
    ["it", Jurisdiction.Italy],
    ["dk", Jurisdiction.Denmark],
    ["es", Jurisdiction.Spain],
    ["cz", Jurisdiction.Czechia],
    ["ad", Jurisdiction.Alderney],
    ["no", Jurisdiction.Norway],
    ["ro", Jurisdiction.Romania],
    ["lv", Jurisdiction.Latvia],
    ["de", Jurisdiction.Germany],
    ["ca-qc", Jurisdiction.Quebec]
]);

class GamevyModel {
    public readonly guestSID: string = "guest";

    public gameName: string = "";
    public gameID: string = "";

    public env: string = "test";
    public region: string = "lon";
    public currency: string = "GBP";
    public includeCurrencyInRules: boolean = false;
    public language: string = "en-GB";
    public platform: string = "gamevy";
    public operator: string;
    public mode: Modes = Modes.FUN;
    public sid: string = this.guestSID;
    public jurisdiction: Jurisdiction;
    public jurisdictionId: string;
    public useGcm: boolean = false;
    public featureFlags: Map<FeatureFlag, FeatureFlagType> = new Map<FeatureFlag, FeatureFlagType>();
    public version: string;
    public symbolMap: Map<number | string, number> = new Map<number, number>();
    public lobbyUrl: string;
    public initialBalance: number;
    public disableRulesCheckError: boolean;
    public multipleRtpSupport: boolean = false;
    public fallbacki18nRules: string = "en-GB";
    public urlParams: UrlParams;

    private realityCheckInterval: number;
    private realityCheckTimePassed: number;
    private rulesUrl: string;
    private operatorsWithCustomRules: string[] = ["lotto_quebec", "milli_piyango", "morocco_lotto", "norsk_tipping", "saska", "health-lottery", "TopSport"];

    private freegames: Map<number, number> = new Map();
    private freegamesWinnings: number = 0;

    public setFreeGames(totalStake: number, freegamesRemaining: number): void {
        this.freegames.set(totalStake, freegamesRemaining);
    }

    public getFreeGames(totalStake: number): number {
        return this.freegames.get(totalStake) || 0;
    }

    public getNextFreeGamesStake(): number | undefined {
        const keys = Array.from(this.freegames.keys());
        const values = Array.from(this.freegames.values());
        const nextGame = values.filter((value) => value > 0)[0];
        const index = values.indexOf(nextGame);
        return keys[index];
    }

    public decrementFreeGames(totalStake: number): number {
        if (this.freegames.has(totalStake)) {
            this.freegames.set(totalStake, this.getFreeGames(totalStake) - 1);
        }
        return this.freegames.get(totalStake) || 0;
    }

    public setFreegamesWinnings(freegamesWinnings: number): void {
        if (freegamesWinnings > 0) {
            this.freegamesWinnings = freegamesWinnings;
        }
    }

    public getFreegamesWinnings(): number {
        return this.freegamesWinnings;
    }

    public getTotalFreeGames(): number {
        let total = 0;
        this.freegames.forEach((value) => total += value);
        return total;
    }

    public clearFreeGames(): void {
        this.freegames = new Map();
    }

    public clearFreeGamesWinnings(): void {
        this.freegamesWinnings = 0;
    }

    public getBaseURL() {
        return `https://bornlucky-${this.env}-${this.region}.gamevy.com`;
    }

    public getCustomURL(endpoint: string) {
        return `${this.getBaseURL()}/${this.gameName}/${endpoint}`;
    }

    public getConfigURL() {
        let url = `${this.getBaseURL()}/${this.gameName}/config?platform=${this.platform}&currency=${this.currency}` + (this.operator ? `&operator=${this.operator}` : ``);

        if (this.jurisdictionId) {
            url = url.concat(`&jurisdiction=${this.jurisdictionId}`);
        }

        return url;
    }

    public getOngoingURL() {
        return `${this.getBaseURL()}/game/ongoing?gameId=${this.gameID}`;
    }

    public getSpinURL() {
        return `${this.getBaseURL()}/${this.gameName}/spin`;
    }

    public getCascadeURL() {
        return `${this.getBaseURL()}/${this.gameName}/cascade`;
    }

    public getBonusURL() {
        return `${this.getBaseURL()}/${this.gameName}/bonus`;
    }

    public getBonusCompleteURL() {
        return `${this.getBaseURL()}/${this.gameName}/complete-bonus`;
    }

    public getRespinURL() {
        return `${this.getBaseURL()}/${this.gameName}/end-round`;
    }

    public getFreespinURL() {
        return `${this.getBaseURL()}/${this.gameName}/end-round`;
    }

    public getBalanceURL() {
        return `${this.getBaseURL()}/payments/${this.platform}/balance?gameId=${this.gameID}&currency=${this.currency}`;
    }

    public getGambleURL() {
        return `${this.getBaseURL()}/${this.gameName}/gamble`;
    }

    public getGambleCollectURL() {
        return `${this.getBaseURL()}/${this.gameName}/collect`;
    }

    public getEndRoundURL() {
        return `${this.getBaseURL()}/${this.gameName}/end-round`;
    }

    public getForceURL(endpoint: string = "fake/spin") {
        return `${this.getBaseURL()}/${this.gameName}/${endpoint}`;
    }

    public prepareRulesUrl(language?: string): Contract<void> {
        if (this.rulesUrl) { return Contract.empty(); }

        if (/(?:(localhost|0\.0\.0\.0|127\.0\.0\.1|192\.168\.))/.test(location.origin)) {
            return Contract.wrap(() => this.setRulesURL(this.buildRulesURL(language)));
        }

        return new Contract((resolve) => {
            const rulesUrl = this.buildRulesURL(language);
            const fallbackUrl = this.buildRulesURL(this.fallbacki18nRules);

            const raceHandler = new RaceHandler(2);

            let rulesValid = false;
            let fallbackValid = false;

            this.testRulesUrl(rulesUrl).then((value: boolean) => {
                if (value) { rulesValid = true; }
                raceHandler.complete();
            });

            this.testRulesUrl(fallbackUrl).then((value: boolean) => {
                if (value) { fallbackValid = true; }
                raceHandler.complete();
            });

            raceHandler.start().then(() => {
                if (rulesValid) {
                    this.setRulesURL(rulesUrl);
                    resolve();
                } else if (fallbackValid) {
                    Logger.warn("Falling back to en-GB rules");
                    this.setRulesURL(fallbackUrl);
                    resolve();
                } else {
                    if (this.disableRulesCheckError) {
                        Logger.error("Rules page not found - suppressing fatal error due to disableRulesCheckError flag");
                        return;
                    }

                    // Timeout needed because this can go so quickly that the error popup hasn't initialised yet
                    Timer.setTimeout(() => { throw new Error("Rules page could not be found."); }, 500);
                }
            });
        });
    }

    public getRulesUrl() {
        return this.rulesUrl + this.getCacheBustString();
    }

    public getRulesCurrencyCode(): string {
        let currencyCode;
        if (this.includeCurrencyInRules) {
            currencyCode = RulesCurrencies.getCurrencyCode(this.currency);
        }
        return currencyCode;
    }

    public setRulesURL(rulesUrl: string) {
        this.rulesUrl = rulesUrl;
    }

    public getRealityCheckOptions(): RealityCheckOptions {
        if (this.realityCheckInterval > 0) {
            return { interval: this.realityCheckInterval, timePassed: this.realityCheckTimePassed };
        }

        return null;
    }

    public loadURLParams() {
        this.urlParams = {};

        const params = queryString.parse(location.search);

        if (params) {
            Object.keys(params).forEach((key: string) => {
                let value = params[key];
                if (isArray(value)) {
                    value = value[value.length - 1];
                }
                this.urlParams[key] = value;

                switch (key) {
                    case "env":
                        this.env = value;
                        break;
                    case "region":
                        this.region = value;
                        break;
                    case "lobbyUrl":
                        this.lobbyUrl = value;
                        break;
                    case "currency":
                        this.currency = value;
                        break;
                    case "lang":
                        this.language = value;
                        break;
                    case "platform":
                        this.platform = value;
                        break;
                    case "operator":
                        this.operator = value;
                        break;
                    case "mode":
                        this.mode = value;
                        break;
                    case "sid":
                        this.sid = value;
                        break;
                    case "jurisdiction":
                        this.jurisdictionId = value;
                        this.jurisdiction = gamevyJurisdiction.get(value);
                        break;
                    case "useGcm":
                        this.useGcm = value;
                        break;
                    case "realityCheckInterval":
                        this.realityCheckInterval = value;
                        break;
                    case "realityCheckTimePassed":
                        this.realityCheckTimePassed = value;
                        break;
                    case "initialBalance":
                        this.initialBalance = value;
                        break;
                    case "disableRulesCheckError":
/////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////
/////////////////////////
//////////////////////////////////
                        break;
                    default:
                        logger.warn("URL parameter not used:", key, value);
                        break;
                }
            });
        }

/////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////
//////////////////
    }

    public getPreloaderMessageKey(): string {
        switch (this.jurisdiction) {
            case Jurisdiction.Alderney:
                return "preloader_" + this.getJuriId(this.jurisdiction);
            case Jurisdiction.UK:
                if (this.operator === "daub") {
                    return "preloader_ad";
                }
                return "";
            default:
                return "";
        }
    }

    protected getJuriId(jurisdiction?: Jurisdiction) {
        for (const [juriId, juri] of gamevyJurisdiction) {
            if (jurisdiction === juri) {
                return juriId;
            }
        }

        return "";
    }

    private buildRulesURL(language: string) {
        const rtp = slotModel.read().regulations.rtp;

        const currencyCode = this.getRulesCurrencyCode();
        const rtpString = this.multipleRtpSupport ? `rtp${rtp}` : undefined;
        let operator = this.operator;
        if (this.operatorsWithCustomRules.indexOf(operator) === -1) {
            operator = undefined;
        }
        const rulesVariant = [this.gameID, currencyCode, rtpString, this.platform, operator, language].filter((x) => x !== undefined).join("-");
        return `https://bornlucky-rules.gamevy.com/${rulesVariant}.html`;
    }

    private testRulesUrl(url: string): Contract<boolean> {
        return new Contract((resolve) => {
            axios.get(url + this.getCacheBustString())
                .then(() => resolve(true))
                .catch(() => resolve(false));
        });
    }

    private getCacheBustString(): string {
        return "?" + Math.random().toString(36).substr(2, 8);
    }
}

export const gamevyModel = new GamevyModel();

class RulesCurrencies {

    public static getCurrencyCode(inputCode: string) {

        if (this.GBPCurrencies.indexOf(inputCode) > -1) {
            return "GBP";
        }

        if (this.NOKCurrencies.indexOf(inputCode) > -1) {
            return "NOK";
        }

        if (this.EURCurrencies.indexOf(inputCode) > -1) {
            return "EUR";
        }

        return undefined;
    }

    private static GBPCurrencies = ["GBP"];
    private static NOKCurrencies = ["NOK", "SEK", "DKK"];
    private static EURCurrencies = ["EUR", "CAD", "NZD", "USD"];
}
